/**
 * Param-compare / backtest-params JWT token storage.
 * PREFERRED: Server issues a one-time `pauth` opaque code; param-redirect sets an httpOnly cookie.
 * Do not pass JWTs in the query string. Session tokens may be read from sessionStorage for legacy pages.
 */
const PARAM_TOKEN_KEY = 'jester_param_token';

/** Get token from sessionStorage (if previously stored; no longer read from `?token=`). */
export function getStoredParamToken(): string | null {
  try {
    return sessionStorage.getItem(PARAM_TOKEN_KEY);
  } catch {
    return null;
  }
}

/**
 * Strip legacy `?token=` from the URL (do not use for auth) and return stored session param JWT if any.
 * Call once on mount for param-compare and backtest-params pages.
 */
export function consumeParamTokenFromUrl(): string | null {
  try {
    const params = new URLSearchParams(window.location.search || '');
    if (params.has('token')) {
      params.delete('token');
      const newSearch = params.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}${window.location.hash || ''}`
        : `${window.location.pathname}${window.location.hash || ''}`;
      window.history.replaceState(null, '', newUrl);
    }
    return getStoredParamToken();
  } catch {
    return getStoredParamToken();
  }
}

/** Get token for API calls: from sessionStorage or from URL (consumes from URL if present). */
export function getParamToken(): string | null {
  const stored = getStoredParamToken();
  if (stored) return stored;
  return consumeParamTokenFromUrl();
}

/** Clear stored param token (e.g. on logout). */
export function clearParamToken(): void {
  try {
    sessionStorage.removeItem(PARAM_TOKEN_KEY);
  } catch {}
}
